import {
  Button,
  H3,
  InputCheckbox,
  InputPhoneNumber,
} from "@sellar-io/components";
import { useFullPageDialogContext } from "../../../../../../../../components/modal/useFullPageDialogContext";
import { useSellarUser } from "../../../../../../../../hooks";

import { useState } from "react";
import { SellarCreditTwoDialogPageEnum } from "../types/SellarCreditTwoDialogPageEnum";
import { useTwoPreverificationAccount } from "../../../../../../../../hooks/api/v2/two/useTwoPreverificationAccount";
import { useSellarCurrentBusiness } from "../../../../../../../../hooks/useSellarCurrentBusiness.shared";
import { useTwoCreatePreverificationAccount } from "../../../../../../../../hooks/api/v2/two/useTwoCreatePreverificationAccount";
import { useBusinessUpdate } from "../../../../../../../../hooks/api/v2/business/useBusinessUpdate";
import { apiV2AssertError } from "../../../../../../../../hooks/api/v2/apiV2Request";
import { Alert } from "../../../../../../../../components/alert/Alert";
import { two } from "@sellar-io/common";
import { useGetTwoCompanyCreditLimitQuote } from "../../../../../../../../hooks/api/v2/two/useGetTwoCompanyCreditLimitQuote";
import { useCreateTwoCustomerUserVerificationLink } from "../../../../../../../../hooks/api/v2/two/useCreateTwoCustomerUserVerficationLink";
import { useUser } from "../../../../../../../../hooks/api/v2/user/useUser";
import { SELLAR_CREDIT_GET_STARTED } from "../../../../../../../../constants/routes";
import {
  SellarCreditTwoDialogPage,
  SellarCreditTwoHeroImage,
} from "./SellarCreditTwoDialogPage";

export const SellarCreditCheckLoginCheck = () => {
  const { user } = useSellarUser();

  const { data: twoPreverificatonAccountData } = useTwoPreverificationAccount();

  const existingTwoPreverificationAccount = twoPreverificatonAccountData;
  const currentUserCreatedTwoPreverificationAccount =
    twoPreverificatonAccountData?.userEmail === user?.email;

  return (
    <SellarCreditTwoDialogPage>
      {/* A two registration has been started by another user */}
      {existingTwoPreverificationAccount &&
        !currentUserCreatedTwoPreverificationAccount && (
          <ChangeAccount
            existingApplicationUserEmail={
              existingTwoPreverificationAccount.userEmail
            }
          />
        )}

      {/* A two registration has been started by this user */}
      {existingTwoPreverificationAccount &&
        currentUserCreatedTwoPreverificationAccount && <VerifyTwoAccount />}

      {/* If no existing application, make a new application */}
      {!existingTwoPreverificationAccount && (
        <CreateTwoPreverificationAccountForm />
      )}
    </SellarCreditTwoDialogPage>
  );
};

const CreateTwoPreverificationAccountForm = () => {
  const twoCreditLimitQuote = useGetTwoCompanyCreditLimitQuote();
  const twoPreverificatonAccountMutation = useTwoCreatePreverificationAccount();
  const createTwoCustomerUserVerificationLink =
    useCreateTwoCustomerUserVerificationLink();

  const { setPage, onDismiss } = useFullPageDialogContext();
  const [confirm, setConfirm] = useState(false);
  const { user } = useSellarUser();

  const { business } = useSellarCurrentBusiness();

  const userQuery = useUser(user?.id ?? 0, { enabled: !!user?.id });

  // TODO: support user mobile number as backup
  const businessPhoneNumberFormatted = two.formatPhoneNumber(
    business?.phoneNumber || userQuery.data?.smsNumber || "",
  );
  const userNameFormatted = two.formatName(user?.name ?? "");
  const invoiceAddressFormatted = two.formatAddress({
    city: business?.InvoiceAddress.city ?? "",
    country: "GB",
    postalCode: business?.InvoiceAddress.postalCode ?? "",
    street: `${business?.InvoiceAddress.name} ${business?.InvoiceAddress.street}`,
  });
  const emailAddressFormatted = two.formatEmail(user?.email ?? "");
  const twoOrganisationNumber = twoCreditLimitQuote.data?.twoOrganisationNumber;

  const isLoading =
    twoPreverificatonAccountMutation.isPending ||
    twoCreditLimitQuote.isPending ||
    createTwoCustomerUserVerificationLink.isPending;

  const hasValidData =
    !!userNameFormatted.data &&
    !!businessPhoneNumberFormatted.data &&
    !!invoiceAddressFormatted.data &&
    !!emailAddressFormatted.data &&
    !!twoOrganisationNumber;

  const onSubmit = () => {
    if (hasValidData) {
      twoPreverificatonAccountMutation
        .mutateAsync({
          invoiceBillingAddress: {
            city: invoiceAddressFormatted.data.city,
            region: business?.InvoiceAddress.region || "   ",
            country: invoiceAddressFormatted.data.country,
            postalCode: invoiceAddressFormatted.data.postal_code,
            streetAddress: invoiceAddressFormatted.data.street_address,
          },
          organizationNumber: twoOrganisationNumber,
          userDetails: {
            firstName: userNameFormatted.data.firstName,
            lastName: userNameFormatted.data.lastName,
            phoneNumber: businessPhoneNumberFormatted.data,
            email: emailAddressFormatted.data,
          },
        })
        .then(() => {
          onDismiss?.();
          // TODO: FIX twoPreverificatonAccountMutation causes the getPreverifcationAccount query to trigger causing the tab to change to VerifyTwoAccount
          const returnUrl = `${import.meta.env.VITE_URL}${SELLAR_CREDIT_GET_STARTED}`;
          // create the verification link and redirect user
          createTwoCustomerUserVerificationLink
            .mutateAsync({ returnUrl })
            .then((result) => {
              window.open(result.redirectUrl, "_blank");
            });
        });
    }
  };

  return (
    <>
      <SellarCreditTwoHeroImage
        src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/login-check.svg"
        width={178}
        height={170}
        className="w-[150px]"
      />
      <H3>Before you go</H3>

      <p>
        It’s important to make sure you are using the correct Sellar account, as
        this is where your verification and company management controls will be
        saved to
      </p>

      {userNameFormatted.error && (
        <Alert
          type="warning"
          title="Invalid name found for user"
          message={
            "Update your 'name' in 'Account settings' before returning to the application"
          }
          className="text-left"
        />
      )}

      {businessPhoneNumberFormatted.error && (
        <BusinessPhoneNumberForm
          businessId={business?.id}
          initialValue={
            business?.phoneNumber || userQuery.data?.smsNumber || ""
          }
        />
      )}

      {invoiceAddressFormatted.error && (
        <Alert
          type="warning"
          title="Invalid invoice address"
          message={`${invoiceAddressFormatted.errorMessage}. Update your 'Invoice address' in 'Business details' before returning to the application`}
          className="text-left"
        />
      )}

      {emailAddressFormatted.error && (
        <Alert
          type="warning"
          title="Invalid email address"
          message={""}
          className="text-left"
        />
      )}

      {twoCreditLimitQuote.error && (
        <Alert
          type="error"
          title="No credit limit quote found"
          message={""}
          className="text-left"
        />
      )}

      <label className="flex w-full cursor-pointer gap-4 rounded-lg border border-indigo-600 bg-indigo-50 p-4 text-left">
        <InputCheckbox
          variant="circular"
          checked={confirm}
          onChange={(event) => setConfirm(event.target.checked)}
          containerClassName="flex-none"
        />
        <p className="break-words">
          I confirm that <b className="break-all">{user?.email}</b> is the
          correct Sellar account and this email can be used to verify my account
          with Two Inc
        </p>
      </label>
      <div className="mt-4 flex w-full flex-col gap-2">
        <Button
          className="w-full"
          disabled={!confirm || !hasValidData || isLoading}
          onClick={onSubmit}
          type="button"
        >
          Get started with Two
        </Button>
        <Button
          color="transparent"
          className="w-full"
          onClick={() => setPage(SellarCreditTwoDialogPageEnum.LOGIN_PROMPT)}
        >
          Use a different login
        </Button>
      </div>

      {twoPreverificatonAccountMutation.error && (
        <Alert
          type="error"
          title="Error creating account"
          message={twoPreverificatonAccountMutation.error.message}
          className="w-full"
        />
      )}
    </>
  );
};

const BusinessPhoneNumberForm = ({
  businessId,
  initialValue,
}: {
  businessId?: number;
  initialValue: string;
}) => {
  const [phoneValue, setPhoneValue] = useState(initialValue);
  const businessUpdateMutation = useBusinessUpdate(businessId);

  const localFormatted = two.formatPhoneNumber(phoneValue);
  const isUkPhoneNumber = phoneValue.startsWith("+44");
  const isValidPhoneNumber = isUkPhoneNumber && !localFormatted.error;

  const onSave = () => {
    if (!isUkPhoneNumber || localFormatted.error || !businessId) {
      return;
    }
    businessUpdateMutation.mutate({ phoneNumber: localFormatted.data });
  };

  return (
    <Alert
      type="warning"
      title="Invalid business phone number found"
      message="Add or update a valid UK business phone number to continue."
      className="w-full text-left"
    >
      <div className="flex flex-col gap-2 overflow-visible">
        <InputPhoneNumber
          value={phoneValue}
          onChange={(phone) => setPhoneValue(phone)}
        />
        {phoneValue && !isUkPhoneNumber && (
          <p className="text-sm text-red-800">
            Enter a UK phone number starting with +44.
          </p>
        )}
        <Button
          type="button"
          className="w-full"
          disabled={
            !isValidPhoneNumber ||
            businessUpdateMutation.isPending ||
            !businessId
          }
          onClick={onSave}
        >
          Save phone number
        </Button>
        {businessUpdateMutation.isError && (
          <p className="text-sm text-red-800">
            {apiV2AssertError(businessUpdateMutation.error)?.response?.data
              .message ?? "Unknown error"}
          </p>
        )}
      </div>
    </Alert>
  );
};

const VerifyTwoAccount = () => {
  const createTwoCustomerUserVerificationLink =
    useCreateTwoCustomerUserVerificationLink();

  return (
    <>
      <SellarCreditTwoHeroImage
        src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/login-check.svg"
        width={178}
        height={170}
        className="w-[150px]"
      />
      <H3>Verify Two Account</H3>

      <p className="break-words">
        An existing account has been created with Two, please verify your
        account by clicking below.
      </p>

      {createTwoCustomerUserVerificationLink.error && (
        <Alert
          type="error"
          title="Could not create user verification link"
          message={"Please refresh page and try again."}
          className="text-left"
        />
      )}

      <div className="mt-4 flex w-full flex-col gap-2">
        <Button
          className="w-full"
          disabled={createTwoCustomerUserVerificationLink.isPending}
          onClick={() => {
            const returnUrl = `${import.meta.env.VITE_URL}${SELLAR_CREDIT_GET_STARTED}`;
            // create the verification link and redirect user
            createTwoCustomerUserVerificationLink
              .mutateAsync({ returnUrl })
              .then((result) => {
                window.open(result.redirectUrl, "_blank");
              });
          }}
        >
          Verify Two account
        </Button>
      </div>
    </>
  );
};

const ChangeAccount = ({
  existingApplicationUserEmail,
}: {
  existingApplicationUserEmail: string;
}) => {
  const { setPage } = useFullPageDialogContext();
  return (
    <>
      <SellarCreditTwoHeroImage
        src="https://sellar.imgix.net/sellar-image-assets/sellar-credit/login-check.svg"
        width={178}
        height={170}
        className="w-[150px]"
      />
      <H3>Existing application found</H3>

      <p className="break-words">
        An existing application has been started with Two, however, the user
        email of the application does not match your email. Please sign in as
        the user with email {existingApplicationUserEmail} to complete your
        application
      </p>

      <div className="mt-4 flex w-full flex-col gap-2">
        <Button
          color="transparent"
          className="w-full"
          onClick={() => setPage(SellarCreditTwoDialogPageEnum.LOGIN_PROMPT)}
        >
          Use a different login
        </Button>
      </div>
    </>
  );
};
